import React, { useEffect, useState } from "react";
import Modal from "../../../shared/components/Modal";
import { User, Mail, AlertCircle } from "lucide-react";

export interface UserSelectionData {
  role: string | null;
  name: string;
  emailId: string;
}

export interface AdminUserModalProps {
  identity: "add_user" | "edit_user" | "view_user" | "access_denied";
  title: string;
  message?: string;
  show: boolean;
  onClose: () => void;
  onConfirm?: (value: UserSelectionData) => void;
  buttonText?: string;
  userName?: string;
  emailAddress?: string;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px 11px 42px',
  border: '1.5px solid #e2e8f0',
  borderRadius: '10px',
  fontSize: '0.9rem',
  color: '#0f172a',
  background: '#f8fafc',
  outline: 'none',
  transition: 'all 0.2s',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 700,
  color: '#475569',
  marginBottom: '7px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const AddAdminUserModal: React.FC<AdminUserModalProps> = ({
  identity,
  title,
  message,
  show,
  onClose,
  onConfirm,
  buttonText = "Save",
  userName = "",
  emailAddress = "",
}) => {
  const [fullName, setFullName] = useState<string>("");
  const [emailId, setEmailId] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [nameFocused, setNameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  const selectedRole = "Admin";

  useEffect(() => {
    if (show) {
      setFullName(userName);
      setEmailId(emailAddress);
      setEmailError(null);
    }
  }, [show, userName, emailAddress]);

  const validateEmail = (email: string): boolean => {
    const trimmed = email.trim();
    const parts = trimmed.split("@");
    if (parts.length !== 2) return false;
    const [localPart, domain] = parts;
    if (!localPart || localPart.length > 64) return false;
    if (localPart.startsWith(".") || localPart.endsWith(".")) return false;
    if (!/^[A-Za-z0-9._%+-]+$/.test(localPart)) return false;
    if (!domain.includes(".")) return false;
    if (domain.startsWith("-") || domain.startsWith(".") || domain.endsWith("-") || domain.endsWith(".")) return false;
    const domainParts = domain.split(".");
    if (domainParts.some((label) => !/^[A-Za-z0-9-]+$/.test(label) || label.length === 0)) return false;
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 1 || tld.length > 63) return false;
    return true;
  };

  const handleConfirm = () => {
    if (identity === "add_user" && !validateEmail(emailId)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError(null);
    if (onConfirm) {
      onConfirm({ role: selectedRole, name: fullName, emailId });
    }
  };

  const isFormValid = fullName.trim() !== "" && (identity === "edit_user" || emailId.trim() !== "");

  const initials = (userName || fullName || "?").charAt(0).toUpperCase();

  return (
    <Modal open={show} onClose={onClose} title={title} width="480px">
      <div style={{ padding: '4px 2px' }}>

        {/* User Banner for edit/view */}
        {(identity === "view_user" || identity === "edit_user") && (
          <div style={{
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '24px',
          }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '14px',
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px', fontWeight: 800, flexShrink: 0,
              backdropFilter: 'blur(4px)',
            }}>
              {initials}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#fff', marginBottom: '3px' }}>{userName}</div>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={13} />
                {emailAddress}
              </div>
            </div>
          </div>
        )}

        {/* Name Field */}
        {(identity === "add_user" || identity === "edit_user") && (
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Full Name</label>
            <div style={{ position: 'relative' }}>
              <User size={16} color={nameFocused ? '#059669' : '#94a3b8'} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'color 0.2s' }} />
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                placeholder="e.g. Jane Smith"
                style={{
                  ...inputStyle,
                  borderColor: nameFocused ? '#059669' : '#e2e8f0',
                  background: nameFocused ? '#fff' : '#f8fafc',
                  boxShadow: nameFocused ? '0 0 0 3px rgba(5,150,105,0.1)' : 'none',
                }}
              />
            </div>
          </div>
        )}

        {/* Email Field */}
        {identity === "add_user" && (
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>Email Address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={16} color={emailFocused ? '#059669' : emailError ? '#ef4444' : '#94a3b8'} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'color 0.2s' }} />
              <input
                type="text"
                value={emailId}
                onChange={(e) => {
                  setEmailId(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                placeholder="e.g. jane@company.com"
                style={{
                  ...inputStyle,
                  borderColor: emailError ? '#ef4444' : emailFocused ? '#059669' : '#e2e8f0',
                  background: emailFocused ? '#fff' : '#f8fafc',
                  boxShadow: emailError ? '0 0 0 3px rgba(239,68,68,0.1)' : emailFocused ? '0 0 0 3px rgba(5,150,105,0.1)' : 'none',
                }}
              />
            </div>
            {emailError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#ef4444', fontSize: '0.8rem', fontWeight: 500 }}>
                <AlertCircle size={13} />
                {emailError}
              </div>
            )}
          </div>
        )}

        {/* Role Badge - compact */}
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Role</label>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 12px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '999px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#059669', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontWeight: 700, color: '#059669', fontSize: '0.82rem', letterSpacing: '0.3px' }}>
              {(identity === "view_user" || identity === "edit_user") ? (message || "Admin") : "Admin"}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: '#f1f5f9', margin: '0 -2px 20px' }} />

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: '10px',
              background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.9rem',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#059669'; (e.currentTarget as HTMLButtonElement).style.color = '#059669'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#e2e8f0'; (e.currentTarget as HTMLButtonElement).style.color = '#475569'; }}
          >
            {identity === 'view_user' ? 'Close' : 'Cancel'}
          </button>
          {identity !== 'view_user' && (
            <button
              onClick={handleConfirm}
              disabled={!isFormValid}
              style={{
                flex: 1, padding: '11px', border: 'none', borderRadius: '10px',
                background: isFormValid ? 'linear-gradient(135deg, #059669, #047857)' : '#cbd5e1',
                color: '#fff', fontWeight: 700, fontSize: '0.9rem',
                cursor: isFormValid ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                boxShadow: isFormValid ? '0 4px 14px rgba(5,150,105,0.25)' : 'none',
              }}
            >
              {buttonText}
            </button>
          )}
        </div>

      </div>
    </Modal>
  );
};

export default AddAdminUserModal;
